import { useState, useCallback } from 'react'
import ReactMarkdown from 'react-markdown'
import { Button } from '@/components'
import { cn } from '@/lib/utils'

interface QuestionOption {
  id: string
  label: string
  description?: string
}

interface Question {
  id: string
  text: string
  type: 'single' | 'multiple' | 'text'
  required?: boolean
  options?: QuestionOption[]
}

interface QuestionsFormData {
  title?: string
  description?: string
  questions: Question[]
}

interface QuestionsFormComponentProps {
  data: QuestionsFormData
  onSubmit: (answers: Record<string, unknown>) => void
}

export default function QuestionsFormComponent({ data, onSubmit }: QuestionsFormComponentProps) {
  const [answers, setAnswers] = useState<Record<string, unknown>>({})
  const [validationErrors, setValidationErrors] = useState<Set<string>>(new Set())

  const handleSingleSelect = useCallback((questionId: string, optionId: string) => {
    setAnswers((prev) => ({ ...prev, [questionId]: optionId }))
    setValidationErrors((prev) => {
      const next = new Set(prev)
      next.delete(questionId)
      return next
    })
  }, [])

  const handleMultipleSelect = useCallback((questionId: string, optionId: string) => {
    setAnswers((prev) => {
      const current = Array.isArray(prev[questionId]) ? (prev[questionId] as string[]) : []
      const isSelected = current.includes(optionId)
      return {
        ...prev,
        [questionId]: isSelected ? current.filter((id) => id !== optionId) : [...current, optionId],
      }
    })
    setValidationErrors((prev) => {
      const next = new Set(prev)
      next.delete(questionId)
      return next
    })
  }, [])

  const handleTextInput = useCallback((questionId: string, value: string) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }))
    setValidationErrors((prev) => {
      const next = new Set(prev)
      next.delete(questionId)
      return next
    })
  }, [])

  const handleSubmit = useCallback(() => {
    const errors = new Set<string>()
    data.questions.forEach((question) => {
      if (question.required) {
        const answer = answers[question.id]
        if (
          !answer ||
          (Array.isArray(answer) && answer.length === 0) ||
          (typeof answer === 'string' && answer.trim() === '')
        ) {
          errors.add(question.id)
        }
      }
    })

    if (errors.size > 0) {
      setValidationErrors(errors)
      return
    }

    // Submit a structured response so the chat history preserves question + answer pairs.
    const structuredResponses: Record<string, { question: string; answer: string }> = {}

    data.questions.forEach((question) => {
      const answerValue = answers[question.id]

      if (
        !answerValue ||
        (Array.isArray(answerValue) && answerValue.length === 0) ||
        (typeof answerValue === 'string' && answerValue.trim() === '')
      ) {
        return
      }

      if (question.type === 'single') {
        const selectedOption = question.options?.find((opt) => opt.id === answerValue)
        structuredResponses[question.id] = {
          question: question.text,
          answer: selectedOption?.label || String(answerValue),
        }
      } else if (question.type === 'multiple') {
        const ids = answerValue as string[]
        const selectedOptions = question.options?.filter((opt) => ids.includes(opt.id))
        structuredResponses[question.id] = {
          question: question.text,
          answer: selectedOptions?.map((opt) => opt.label).join(', ') || ids.join(', '),
        }
      } else if (question.type === 'text') {
        structuredResponses[question.id] = {
          question: question.text,
          answer: String(answerValue),
        }
      }
    })

    onSubmit(structuredResponses)
  }, [answers, data.questions, onSubmit])

  const canSubmit = data.questions
    .filter((q) => q.required)
    .every((q) => {
      const answer = answers[q.id]
      return (
        !!answer &&
        (Array.isArray(answer)
          ? answer.length > 0
          : typeof answer === 'string'
            ? answer.trim() !== ''
            : true)
      )
    })

  if (!data.questions || !Array.isArray(data.questions) || data.questions.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-card p-4">
        <p className="text-body text-danger">
          Error: No questions available. Please try again.
        </p>
        <pre className="mt-2 text-mono-sm text-muted-foreground overflow-x-auto">
          {JSON.stringify(data, null, 2)}
        </pre>
      </div>
    )
  }

  return (
    <div className="rounded-lg border border-border bg-card p-4 flex flex-col gap-4">
      {data.title && (
        <h3 className="text-section text-foreground">{data.title}</h3>
      )}

      {data.description && (
        <div className="rounded-md border border-border bg-background p-3">
          <div className="text-body text-foreground markdown-content break-words">
            <ReactMarkdown
              components={{
                p: ({ node: _node, ...props }) => (
                  <p className="my-2 first:mt-0 last:mb-0" {...props} />
                ),
                strong: ({ node: _node, ...props }) => (
                  <strong className="font-semibold text-foreground" {...props} />
                ),
                em: ({ node: _node, ...props }) => <em className="italic" {...props} />,
                code: ({ node: _node, ...props }) => (
                  <code className="rounded-sm bg-muted px-1.5 py-0.5 text-mono-sm" {...props} />
                ),
              }}
            >
              {data.description}
            </ReactMarkdown>
          </div>
        </div>
      )}

      {data.description && (
        <div className="border-b border-border pb-3 text-caption text-muted-foreground text-center">
          Please answer the following questions
        </div>
      )}

      <div className="flex flex-col gap-4">
        {data.questions.map((question) => (
          <div key={question.id} className="flex flex-col gap-2">
            <label className="text-label text-foreground">
              {question.text}
              {question.required && <span className="text-danger"> *</span>}
            </label>

            {validationErrors.has(question.id) && (
              <p className="text-caption text-danger">This question is required</p>
            )}

            {question.type === 'single' && question.options && (
              <div className="flex flex-col gap-2">
                {question.options.map((option) => {
                  const selected = answers[question.id] === option.id
                  return (
                    <label
                      key={option.id}
                      className={cn(
                        'flex items-start gap-2 p-2 rounded-md border border-border cursor-pointer transition-colors',
                        selected ? 'bg-accent' : 'bg-transparent hover:bg-hover',
                      )}
                    >
                      <input
                        type="radio"
                        name={question.id}
                        value={option.id}
                        checked={selected}
                        onChange={() => handleSingleSelect(question.id, option.id)}
                        className="mt-1 accent-primary"
                      />
                      <div className="flex-1">
                        <div className="text-body text-foreground">{option.label}</div>
                        {option.description && (
                          <div className="text-caption text-muted-foreground mt-0.5">
                            {option.description}
                          </div>
                        )}
                      </div>
                    </label>
                  )
                })}
              </div>
            )}

            {question.type === 'multiple' && question.options && (
              <div className="flex flex-col gap-2">
                {question.options.map((option) => {
                  const current = Array.isArray(answers[question.id])
                    ? (answers[question.id] as string[])
                    : []
                  const isChecked = current.includes(option.id)
                  return (
                    <label
                      key={option.id}
                      className={cn(
                        'flex items-start gap-2 p-2 rounded-md border border-border cursor-pointer transition-colors',
                        isChecked ? 'bg-accent' : 'bg-transparent hover:bg-hover',
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => handleMultipleSelect(question.id, option.id)}
                        className="mt-1 accent-primary"
                      />
                      <div className="flex-1">
                        <div className="text-body text-foreground">{option.label}</div>
                        {option.description && (
                          <div className="text-caption text-muted-foreground mt-0.5">
                            {option.description}
                          </div>
                        )}
                      </div>
                    </label>
                  )
                })}
              </div>
            )}

            {question.type === 'text' && (
              <textarea
                className={cn(
                  'w-full rounded-md bg-background text-body text-foreground p-2 resize-none focus:outline-none focus:ring-1 focus:ring-ring',
                  validationErrors.has(question.id) ? 'border border-danger' : 'border border-border',
                )}
                rows={3}
                placeholder="Type your answer..."
                value={(answers[question.id] as string) || ''}
                onChange={(e) => handleTextInput(question.id, e.target.value)}
              />
            )}
          </div>
        ))}
      </div>

      <div className="flex flex-col gap-1.5">
        <Button size="sm" className="self-start" onClick={handleSubmit} disabled={!canSubmit}>
          Submit Answers
        </Button>
        <p className="text-caption text-muted-foreground">
          Or ignore this form and continue typing in the chat below
        </p>
      </div>
    </div>
  )
}
